import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useShop } from "@/context/Shop";
import { useFetch } from "@/hooks/useFetch";
import { useSeo } from "@/hooks/useSeo";
import { media } from "@/lib/api";
import { ProductRail, SkeletonGrid } from "@/components/store/ProductCard";

const HERO = {
  women: ["https://images.unsplash.com/photo-1722188423919-13925e2de861", "Precision and ease: coats, gowns and leather goods for the season ahead."],
  men: ["https://images.unsplash.com/photo-1619603364904-c0498317e145", "Tailoring with intent, outerwear with character and shoes made to last."],
};

export default function GenderLanding({ gender: g }) {
  const params = useParams();
  const gender = g || params.gender;
  const { nav } = useShop();
  const { data, loading } = useFetch("/store/products", { gender, page_size: 8, sort: "newest" });
  const { data: sale } = useFetch("/store/products", { gender, sale: "true", page_size: 4 });
  const cats = nav?.categories?.[gender] || [];
  const cols = (nav?.collections || []).filter((c) => c.gender === gender || c.gender === "all");
  const label = gender === "women" ? "Women" : "Men";
  useSeo(`${label}'s designer fashion`, HERO[gender][1]);
  return (
    <div data-testid={`landing-${gender}`}>
      <section className="relative h-[62vh] min-h-[420px] overflow-hidden bg-white grain">
        <img src={media(HERO[gender][0], 2000)} alt="" className="absolute inset-0 w-full h-full object-cover object-[50%_30%]" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-black/10 to-transparent" />
        <div className="relative container-lux h-full flex flex-col justify-end pb-14 text-white">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-[11px] uppercase tracking-[0.2em]">The {label}'s edit</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="text-6xl sm:text-7xl lg:text-8xl font-medium mt-2">{label}</motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="max-w-md text-white/85 mt-4">{HERO[gender][1]}</motion.p>
          <Link to={`/${gender}/all`} className="mt-8 inline-flex w-fit items-center bg-white text-foreground h-12 px-8 text-[13px] font-semibold uppercase tracking-[0.14em] rounded-md hover:bg-white/90 transition-colors" data-testid={`landing-${gender}-shop-all-button`}>Shop all {label.toLowerCase()}</Link>
        </div>
      </section>

      <section className="container-lux py-12">
        <p className="eyebrow mb-5">Shop by category</p>
        <div className="flex flex-wrap gap-3" data-testid={`landing-${gender}-categories`}>
          {cats.map((c) => (
            <Link key={c.slug} to={`/${gender}/${c.slug}`} className="rounded-full border px-5 py-2.5 text-sm hover:bg-foreground hover:text-background transition-colors" data-testid={`landing-category-${c.slug}`}>
              {c.name} <span className="text-muted-foreground ml-1 tabular">{c.count}</span>
            </Link>
          ))}
          <Link to={`/${gender}/all?sale=true`} className="rounded-full border border-[hsl(var(--destructive))]/40 text-[hsl(var(--destructive))] px-5 py-2.5 text-sm hover:bg-[hsl(var(--destructive))] hover:text-white transition-colors">Sale</Link>
        </div>
      </section>

      {loading ? <div className="container-lux"><SkeletonGrid /></div> : <ProductRail title="New in" items={data?.items} link={`/${gender}/all?new=true`} testid={`landing-${gender}-new`} />}

      {cols.length > 0 && (
        <section className="container-lux grid md:grid-cols-2 gap-6">
          {cols.slice(0, 2).map((c) => (
            <Link key={c.slug} to={`/collections/${c.slug}`} className="group relative overflow-hidden rounded-md aspect-[16/10] bg-white" data-testid={`landing-collection-${c.slug}`}>
              {c.hero_image_url && <img src={media(c.hero_image_url, 1200)} alt="" className="absolute inset-0 w-full h-full object-cover img-zoom" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute bottom-6 left-6 text-white"><p className="text-[11px] uppercase tracking-[0.18em]">Collection</p><p className="font-display text-4xl">{c.name}</p></div>
            </Link>
          ))}
        </section>
      )}
      <ProductRail title="Sale" items={sale?.items} link={`/${gender}/all?sale=true`} testid={`landing-${gender}-sale`} linkLabel="Shop sale" />
    </div>
  );
}
